import { StyleSheet } from "@react-pdf/renderer";

// Proposal deck styles — forked from src/components/PrepPack/pdfStyles.ts
// (same INK/GOLD house palette) plus the PR-011 deck's black full-bleed
// cover/closing pages, stat tiles, three-column pricing, and process
// timeline. Portrait A4 like the other house PDFs.

export const INK = "#1C1A17";
export const MUTED = "#5C574F";
export const HINT = "#9B958C";
export const GOLD = "#A58450";
export const GOLD_SOFT = "#C9A96A";
export const RULE_SOFT = "#E5E1DB";
export const TILE_BG = "#F4F0E9";
export const PAGE_BG = "#FFFFFF";
export const BLACK_BG = "#0B0A09";

// Brand-themeable factory: the RMN portal passes the resolved brand palette
// (literal hexes — CSS var() strings must never reach @react-pdf) so the
// deck's accent colors follow the white-label theme. Called with no
// argument it reproduces the house palette exactly (the `styles` export
// below stays byte-identical for existing consumers).
export interface PdfAccentPalette {
    // Primary accent (rules, badges, stat values) — house #A58450.
    GOLD_DARK: string;
    // Lighter accent (cover title, bullets, dark-callout titles).
    GOLD: string;
}

export const makePdfStyles = (palette?: PdfAccentPalette) => {
    const gold = palette ? palette.GOLD_DARK : GOLD;
    const goldSoft = palette ? palette.GOLD : GOLD_SOFT;
    return StyleSheet.create({
    page: {
        backgroundColor: PAGE_BG,
        color: INK,
        paddingTop: 42,
        paddingBottom: 52,
        paddingHorizontal: 48,
        fontFamily: "Lato",
        fontSize: 10,
        lineHeight: 1.5,
    },
    blackPage: {
        backgroundColor: BLACK_BG,
        color: "#FFFFFF",
        padding: 60,
        fontFamily: "Lato",
        fontSize: 10,
        lineHeight: 1.5,
        justifyContent: "center",
    },

    // Cover / closing (black pages)
    coverCompany: {
        fontFamily: "Oswald-Bold",
        fontSize: 30,
        color: "#FFFFFF",
        textTransform: "uppercase",
        letterSpacing: 1,
    },
    coverTitle: {
        marginTop: 16,
        fontFamily: "Oswald-Medium",
        fontSize: 17,
        color: goldSoft,
        textTransform: "uppercase",
        letterSpacing: 1.2,
    },
    coverMeta: {
        marginTop: 10,
        fontFamily: "Lato",
        fontSize: 11,
        color: "#B5AFA5",
    },
    coverRule: {
        marginTop: 26,
        width: 120,
        height: 2,
        backgroundColor: goldSoft,
    },

    // Section scaffolding
    eyebrow: {
        fontFamily: "Lato-Bold",
        fontSize: 9,
        color: MUTED,
        textTransform: "uppercase",
        letterSpacing: 1,
        marginBottom: 4,
    },
    headRule: {
        height: 2,
        backgroundColor: gold,
        marginBottom: 16,
        width: 64,
    },
    sectionTitle: {
        fontFamily: "Oswald-Bold",
        fontSize: 17,
        color: INK,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 10,
        lineHeight: 1.25,
    },
    sub: {
        fontFamily: "Lato",
        fontSize: 10,
        color: MUTED,
        marginBottom: 14,
        lineHeight: 1.55,
    },
    paragraph: {
        fontFamily: "Lato",
        fontSize: 10,
        color: INK,
        marginBottom: 8,
        lineHeight: 1.55,
    },
    footerNote: {
        position: "absolute",
        bottom: 26,
        left: 48,
        right: 48,
        flexDirection: "row",
        justifyContent: "space-between",
        fontSize: 8,
        color: HINT,
    },

    // Stat tiles
    statRow: {
        flexDirection: "row",
        gap: 10,
        marginTop: 8,
        marginBottom: 8,
    },
    statTile: {
        flex: 1,
        backgroundColor: TILE_BG,
        borderRadius: 6,
        padding: 14,
    },
    statValue: {
        fontFamily: "Oswald-Bold",
        fontSize: 22,
        color: gold,
        marginBottom: 4,
    },
    statLabel: {
        fontFamily: "Lato",
        fontSize: 9,
        color: INK,
        lineHeight: 1.45,
    },
    statSource: {
        fontFamily: "Lato",
        fontSize: 7.5,
        color: HINT,
        marginTop: 6,
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },

    // Selections / key-value rail
    kvBlock: {
        backgroundColor: TILE_BG,
        borderRadius: 6,
        padding: 12,
        marginBottom: 10,
    },
    kvLabel: {
        fontFamily: "Lato-Bold",
        fontSize: 8,
        color: HINT,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        marginBottom: 2,
    },
    kvValue: {
        fontFamily: "Lato-Bold",
        fontSize: 11,
        color: INK,
        marginBottom: 6,
    },

    // Video mix rows
    mixRow: {
        flexDirection: "row",
        borderWidth: 0.5,
        borderColor: RULE_SOFT,
        borderRadius: 6,
        padding: 10,
        marginBottom: 7,
        alignItems: "flex-start",
    },
    mixBadge: {
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: gold,
        color: "#FFF",
        fontFamily: "Lato-Bold",
        fontSize: 9,
        textAlign: "center",
        paddingTop: 3,
        marginRight: 10,
    },
    mixBadgeOptional: {
        backgroundColor: RULE_SOFT,
        color: MUTED,
    },
    mixTitle: {
        fontFamily: "Lato-Bold",
        fontSize: 10.5,
        color: INK,
    },
    mixMeta: {
        fontFamily: "Lato",
        fontSize: 8.5,
        color: HINT,
        marginTop: 1,
    },
    mixReason: {
        fontFamily: "Lato",
        fontSize: 9,
        color: MUTED,
        marginTop: 3,
        lineHeight: 1.45,
    },
    mixPrice: {
        fontFamily: "Lato-Bold",
        fontSize: 10,
        color: MUTED,
        marginLeft: 8,
    },

    // Where-it-lives table
    tableHead: {
        flexDirection: "row",
        backgroundColor: INK,
        borderRadius: 3,
    },
    tableHeadCell: {
        paddingHorizontal: 10,
        paddingVertical: 6,
        color: "#FFF",
        fontFamily: "Lato-Bold",
        fontSize: 8.5,
        textTransform: "uppercase",
        letterSpacing: 0.4,
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: RULE_SOFT,
    },
    tableCell: {
        paddingHorizontal: 10,
        paddingVertical: 7,
        fontSize: 9.5,
        color: INK,
    },

    // Black callout band
    darkCallout: {
        backgroundColor: INK,
        borderRadius: 6,
        padding: 14,
        marginTop: 12,
    },
    darkCalloutTitle: {
        fontFamily: "Lato-Bold",
        fontSize: 10.5,
        color: goldSoft,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        marginBottom: 4,
    },
    darkCalloutBody: {
        fontFamily: "Lato",
        fontSize: 9.5,
        color: "#D9D4CC",
        lineHeight: 1.55,
    },

    // Investment columns
    priceCols: {
        flexDirection: "row",
        gap: 10,
        marginTop: 10,
    },
    priceCol: {
        flex: 1,
        borderWidth: 0.5,
        borderColor: RULE_SOFT,
        borderRadius: 6,
        padding: 12,
    },
    priceColSelected: {
        borderWidth: 1.5,
        borderColor: gold,
        backgroundColor: "#FBF8F2",
    },
    priceColTitle: {
        fontFamily: "Oswald-Bold",
        fontSize: 11,
        color: INK,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        marginBottom: 6,
        textAlign: "center",
    },
    priceColAmount: {
        fontFamily: "Oswald-Bold",
        fontSize: 20,
        color: INK,
        textAlign: "center",
        marginBottom: 8,
    },
    priceColLine: {
        flexDirection: "row",
        marginBottom: 4,
    },
    priceColCheck: {
        width: 12,
        fontSize: 9,
        color: gold,
    },
    priceColLineText: {
        flex: 1,
        fontSize: 8.5,
        color: INK,
        lineHeight: 1.4,
    },
    selectedBadge: {
        fontFamily: "Lato-Bold",
        fontSize: 7.5,
        color: "#FFF",
        backgroundColor: gold,
        borderRadius: 3,
        paddingVertical: 2,
        paddingHorizontal: 6,
        textAlign: "center",
        alignSelf: "center",
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: 0.6,
    },

    // Process timeline
    processRow: {
        flexDirection: "row",
        gap: 8,
        marginTop: 10,
    },
    processStep: {
        flex: 1,
        borderWidth: 0.5,
        borderColor: RULE_SOFT,
        borderRadius: 6,
        padding: 10,
    },
    processStepNum: {
        fontFamily: "Oswald-Bold",
        fontSize: 9,
        color: "#FFF",
        backgroundColor: gold,
        borderRadius: 3,
        paddingVertical: 2,
        paddingHorizontal: 6,
        alignSelf: "flex-start",
        marginBottom: 6,
    },
    processStepTitle: {
        fontFamily: "Lato-Bold",
        fontSize: 9.5,
        color: INK,
        textTransform: "uppercase",
        letterSpacing: 0.3,
        marginBottom: 4,
    },
    processStepBody: {
        fontFamily: "Lato",
        fontSize: 8.5,
        color: MUTED,
        lineHeight: 1.45,
    },

    // Bullets (next steps etc.)
    bullet: {
        flexDirection: "row",
        marginBottom: 8,
    },
    bulletArrow: {
        width: 16,
        fontFamily: "Lato-Bold",
        fontSize: 10,
        color: goldSoft,
    },
    bulletText: {
        flex: 1,
        fontSize: 10.5,
        lineHeight: 1.6,
    },
});
};

export const styles = makePdfStyles();
